import React, { useState, useEffect, useRef, useCallback } from "react";
import "./index.scss";
import { Spin, Alert, Tag, Empty, Button, message, Input } from "antd";
import {
  EyeOutlined,
  CopyOutlined,
  LeftOutlined,
  RightOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  getVisualizationData,
  PlanData,
  updateVisualizationState,
} from "@/api/custom";
import dayjs from "dayjs";
import { useTheme } from "@/contexts/ThemeContext";

const MOBILE_BREAKPOINT = 768;

const Planning: React.FC = () => {
  const { theme } = useTheme();
  const [visualizationData, setVisualizationData] = useState<PlanData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlanIndex, setSelectedPlanIndex] = useState<number>(0);
  const [hasData, setHasData] = useState<boolean>(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isSavingAnswers, setIsSavingAnswers] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean>(
    window.innerWidth <= MOBILE_BREAKPOINT,
  );
  const [mobileShowDetail, setMobileShowDetail] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      if (!mobile) {
        setMobileShowDetail(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setHasData(true);

      try {
        const visualizationResponse = await getVisualizationData();

        if (
          visualizationResponse &&
          Array.isArray(visualizationResponse) &&
          visualizationResponse.length > 0
        ) {
          const sortedData = [...visualizationResponse].sort(
            (a, b) => b.sharedAt - a.sharedAt,
          );
          setVisualizationData(sortedData);
        } else {
          setHasData(false);
        }
      } catch (err) {
        console.error("Error fetching visualization data:", err);
        setError("Failed to load visualization data. Please try again later.");
        setHasData(false);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleIframeLoad = () => {
    const iframe = iframeRef.current;
    if (iframe && iframe.contentWindow) {
      try {
        // send initialize message
        iframe.contentWindow.postMessage("initialize", "*");

        // if iframe defines updateChart function, call it
        if ("updateChart" in iframe.contentWindow) {
          (iframe.contentWindow as any).updateChart();
        }

        // dynamically adjust iframe height
        const iframeDocument =
          iframe.contentDocument || iframe.contentWindow.document;
        if (iframeDocument && iframeDocument.body) {
          const height = iframeDocument.body.scrollHeight;
          iframe.style.height = `${height}px`;
        }
      } catch (e) {
        console.error("chart initialization failed:", e);
      }
    }
  };

  const formatDate = (timestamp: number) => {
    return dayjs(timestamp * 1000).format("MMMM D, YYYY");
  };

  const extractPlanName = (fileName: string) => {
    // from file name to get plan name
    const nameParts = fileName.split("_");
    return nameParts[1] || "Financial Plan";
  };

  const renderPlanHistory = () => {
    return (
      <div className="plan-history-container">
        <div className="plan-history-header">
          <span className="plan-history-title">Plan History</span>
          <span className="plan-history-tips">
            {visualizationData.length} snapshots available
          </span>
        </div>
        <div className="plan-list">
          {visualizationData.map((plan, index) => (
            <div
              key={index}
              className={`plan-item ${selectedPlanIndex === index ? "selected" : ""}`}
              onClick={() => setSelectedPlanIndex(index)}
            >
              <div className="plan-name">
                {plan.title && plan.title !== "Untitled"
                  ? plan.title
                  : extractPlanName(plan.visualizationHtml.fileName)}
              </div>
              <div className="plan-date">{formatDate(plan.sharedAt)}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderTitle = () => {
    if (
      !visualizationData ||
      visualizationData.length === 0 ||
      selectedPlanIndex >= visualizationData.length
    ) {
      return null;
    }

    const selectedPlan = visualizationData[selectedPlanIndex];

    return (
      <div className="title-container">
        <div className="title-text">
          {selectedPlan.title && selectedPlan.title !== "Untitled"
            ? selectedPlan.title
            : extractPlanName(selectedPlan.visualizationHtml.fileName)}
        </div>
      </div>
    );
  };

  const renderCanvasSnapshot = () => {
    if (
      !visualizationData ||
      visualizationData.length === 0 ||
      selectedPlanIndex >= visualizationData.length
    ) {
      return null;
    }

    const selectedPlan = visualizationData[selectedPlanIndex];

    return (
      <div className="canvas-snapshot">
        <div className="snapshot-icon">
          <CopyOutlined />
        </div>
        <div className="snapshot-content">
          <div className="snapshot-title">
            Canvas Snapshot: Retirement Projection
          </div>
          <div className="snapshot-subtitle">
            {/* Shared by Sarah Johnson, CFP® on {formatDate(selectedPlan.sharedAt)} */}
            Shared on {formatDate(selectedPlan.sharedAt)}
          </div>
        </div>
        <div className="snapshot-tag">
          <Tag icon={<EyeOutlined />} color="default">
            View Only
          </Tag>
        </div>
      </div>
    );
  };

  const getPlanTitle = useCallback(
    (plan: PlanData) =>
      plan.title && plan.title !== "Untitled"
        ? plan.title
        : extractPlanName(plan.visualizationHtml.fileName),
    [],
  );

  const filteredPlans = visualizationData.filter((plan) => {
    if (!searchQuery.trim()) return true;
    const title = getPlanTitle(plan).toLowerCase();
    return title.includes(searchQuery.toLowerCase());
  });

  const handleMobilePlanSelect = (index: number) => {
    const plan = filteredPlans[index];
    if (!plan) return;
    let realIndex = visualizationData.indexOf(plan);
    if (realIndex === -1 && plan.id != null) {
      realIndex = visualizationData.findIndex((p) => p.id === plan.id);
    }
    if (realIndex === -1) return;
    setSelectedPlanIndex(realIndex);
    setMobileShowDetail(true);
  };

  const handleMobileBack = () => {
    setMobileShowDetail(false);
  };

  const renderMobilePlanList = () => {
    return (
      <div className="mobile-plan-list-view">
        <div className="mobile-search-bar">
          <SearchOutlined className="mobile-search-icon" />
          <Input
            className="mobile-search-input"
            placeholder="Search planning…"
            variant="borderless"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="mobile-plan-cards">
          {filteredPlans.map((plan, index) => (
            <div
              key={plan.id || index}
              className="mobile-plan-card"
              onClick={() => handleMobilePlanSelect(index)}
            >
              <div className="mobile-plan-card-content">
                <div className="mobile-plan-card-title">
                  {getPlanTitle(plan)}
                </div>
                <div className="mobile-plan-card-subtitle">
                  {formatDate(plan.sharedAt)}
                </div>
              </div>
              <RightOutlined className="mobile-plan-card-arrow" />
            </div>
          ))}
          {filteredPlans.length === 0 && (
            <Empty
              description="No matching plans"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </div>
      </div>
    );
  };

  const renderMobileDetailView = () => {
    const currentPlan = visualizationData[selectedPlanIndex];
    if (!currentPlan) return null;

    return (
      <div className="mobile-detail-view">
        <div className="mobile-detail-header">
          <div className="mobile-detail-back" onClick={handleMobileBack}>
            <LeftOutlined />
          </div>
          <div className="mobile-detail-title">{getPlanTitle(currentPlan)}</div>
        </div>
        <div className="visualization-container">
          <iframe
            ref={iframeRef}
            className="visualization-iframe"
            title="Financial Planning Visualization"
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
            srcDoc={currentPlan.visualizationHtml?.sourceCode || ""}
            onLoad={handleIframeLoad}
          />
        </div>
        {currentPlan.visualizationHtml?.sourceCode && (
          <Button
            loading={isSavingAnswers}
            onClick={getInputStates}
            style={{ color: "white" }}
          >
            Save
          </Button>
        )}
      </div>
    );
  };

  const containerClass = `planning-container ${theme}-theme`;

  if (loading) {
    return (
      <div className={containerClass}>
        <div className="loading-container">
          <Spin size="large" tip="loading data..." />
        </div>
      </div>
    );
  }

  if (!hasData || !visualizationData || visualizationData.length === 0) {
    return (
      <div className={containerClass}>
        <Empty
          description="No financial plan"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  if (error) {
    return <Alert message="error" description={error} type="error" showIcon />;
  }

  const currentSourceCode =
    visualizationData[selectedPlanIndex]?.visualizationHtml?.sourceCode || "";

  async function getInputStates() {
    // Access the iframe
    const iframe = iframeRef.current;
    if (!iframe) {
      return;
    }

    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!iframeDoc) {
      return;
    }

    setIsSavingAnswers(true);
    const inputs = iframeDoc.querySelectorAll("input");

    const inputValues: Record<string, any> = {};
    inputs.forEach((input) => {
      if (input.type === "checkbox" || input.type === "radio") {
        inputValues[input.id] = input.checked;
      } else {
        inputValues[input.id] = input.value;
      }
    });

    try {
      await updateVisualizationState(
        visualizationData[selectedPlanIndex].id,
        inputValues,
      );
      message.success("Successfully updated the answers.");
    } catch (e) {
      message.error("Failed to update answers");
    }
    setIsSavingAnswers(false);
  }

  // Mobile layout: two-view (list ↔ detail)
  if (isMobile) {
    return (
      <div className={containerClass}>
        {mobileShowDetail ? renderMobileDetailView() : renderMobilePlanList()}
      </div>
    );
  }

  // Desktop layout: unchanged
  return (
    <div className={containerClass}>
      {renderPlanHistory()}
      {/* {renderCanvasSnapshot()} */}
      {renderTitle()}
      <div className="visualization-container">
        <iframe
          ref={iframeRef}
          className="visualization-iframe"
          title="Financial Planning Visualization"
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
          srcDoc={currentSourceCode}
          onLoad={handleIframeLoad}
        />
      </div>
      {currentSourceCode && (
        <Button
          loading={isSavingAnswers}
          onClick={getInputStates}
          style={{ color: "white" }}
        >
          Save
        </Button>
      )}
    </div>
  );
};

export default Planning;
